/**
 * APEX v7.6.0 — Season Import
 *
 * Import paginé de toutes les games d'une saison (filtrées par patch).
 *
 * Stratégie :
 *   1. Récupère les match IDs par batch de 100, depuis startTime (4 janvier 2026)
 *   2. Pour chaque match ID non encore en DB :
 *      - Récupère details + timeline en parallèle (2 calls)
 *      - Parse et filtre par patch (saison 26.x uniquement)
 *   3. Throttling : 5 calls en parallèle toutes les 6 secondes
 *      → respect du rate limit Personal Key (100/2min)
 *   4. Stop possible via flag aborter
 *   5. Reprise naturelle via dedupe riot_match_id en DB
 *
 * Usage :
 *   const importer = createSeasonImporter({...});
 *   await importer.run();
 *   importer.abort(); // pour stopper
 */

import { getMatchIds, getMatchDetails, getMatchTimeline, parseMatchToApexGame } from './riot';

// Timestamp du 4 janvier 2026 en secondes UNIX (début saison 2026)
export const SEASON_2026_START_TIMESTAMP = Math.floor(new Date('2026-01-04T00:00:00Z').getTime() / 1000);

// Patch valide pour la saison 2026 (filtre côté client si l'API renvoie des games hors saison)
// ⚠️ v7.7.6 fix : le gameVersion Riot pour la saison 2026 est "16.X.X.X"
// (et NON "26.X" comme supposé initialement). Les vraies données sont "16.x".
const SEASON_PATCH_PREFIX = '16.';

// ───────────────────────────────────────────────────────────
// Throttling — RATE LIMIT RIOT PERSONAL KEY = 100 req / 2 min
// ───────────────────────────────────────────────────────────
// ⚠️ v7.7 fix : chaque match importé = 2 appels API (details + timeline).
// L'ancien réglage (5 matchs / 6s) faisait 10 calls/6s = 200 calls/2min,
// soit 2x la limite → 429 systématique.
//
// Nouveau calcul (cible ~90 calls / 2 min, marge de sécurité 10%) :
//   - 2 matchs par batch         = 4 appels API par batch
//   - 1 batch toutes les 6 s     = 4 appels / 6 s
//   - = 40 appels / minute       = 80 appels / 2 min ✅ (sous 100)
//   + le listing (1 call / 1.5 s) reste marginal et borné.
//
// C'est plus lent (~2 matchs / 6 s ≈ 20 matchs/min), mais SANS 429.
const BATCH_SIZE = 2;
const BATCH_INTERVAL_MS = 6000;
const MATCH_IDS_BATCH = 100; // max Riot pour le listing

// Backoff si on se prend quand même un 429 (defense-in-depth)
const RATE_LIMIT_BACKOFF_MS = 30000; // 30 s

/**
 * Helper : promet une pause de N ms.
 */
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new Error('aborted'));
      }, { once: true });
    }
  });
}

/**
 * Détecte une erreur de rate limit (429) dans une exception.
 */
function isRateLimitError(err) {
  if (!err) return false;
  const msg = (err.message || String(err)).toLowerCase();
  return msg.includes('429') || msg.includes('rate limit') || msg.includes('too many requests');
}

/**
 * Crée un importateur de saison.
 *
 * @param {object} opts
 * @param {string} opts.puuid - PUUID du joueur
 * @param {string} opts.platform - 'europe' (default)
 * @param {number} opts.queue - 420 (SoloQ) ou 440 (Flex)
 * @param {Set<string>} opts.existingMatchIds - match_ids déjà en DB (pour dedupe)
 * @param {function} opts.onProgress - callback({ phase, current, total, etaMs, fetchedIds, skippedDedupe, skippedPatch, imported, errors })
 * @param {function} opts.onGame - callback(parsedGame) — appelé pour chaque game importable
 */
export function createSeasonImporter(opts) {
  const {
    puuid,
    platform = 'europe',
    queue = 420,
    existingMatchIds = new Set(),
    onProgress = () => {},
    onGame = () => {}
  } = opts;

  const aborter = new AbortController();
  const signal = aborter.signal;

  const state = {
    phase: 'idle', // idle | listing | importing | done | aborted | error
    matchIds: [],
    current: 0,
    total: 0,
    fetchedIds: 0,
    skippedDedupe: 0,
    skippedPatch: 0,
    imported: 0,
    errors: 0,
    startedAt: null,
    aborted: false
  };

  function emit() {
    let etaMs = null;
    if (state.startedAt && state.current > 0 && state.total > 0) {
      const elapsed = Date.now() - state.startedAt;
      const perItem = elapsed / state.current;
      etaMs = Math.max(0, (state.total - state.current) * perItem);
    }
    onProgress({ ...state, etaMs });
  }

  async function listAllMatchIds() {
    state.phase = 'listing';
    emit();

    let allIds = [];
    let start = 0;
    let hasMore = true;

    while (hasMore && !signal.aborted) {
      const batch = await getMatchIds(puuid, {
        platform,
        queue,
        count: MATCH_IDS_BATCH,
        start,
        startTime: SEASON_2026_START_TIMESTAMP
      });
      if (!Array.isArray(batch) || batch.length === 0) {
        hasMore = false;
        break;
      }
      allIds = allIds.concat(batch);
      state.fetchedIds = allIds.length;
      emit();

      if (batch.length < MATCH_IDS_BATCH) {
        hasMore = false;
      } else {
        start += MATCH_IDS_BATCH;
        // pause entre les listings (1 call vers Riot)
        await sleep(1500, signal);
      }
    }

    return allIds;
  }

  async function importOneMatch(matchId, isRetry = false) {
    try {
      // Skip si déjà en DB
      if (existingMatchIds.has(matchId)) {
        state.skippedDedupe++;
        return;
      }

      const [details, timeline] = await Promise.all([
        getMatchDetails(matchId, platform),
        getMatchTimeline(matchId, platform)
      ]);

      const parsed = parseMatchToApexGame(details, timeline, puuid);

      // Filtre saison 26.x
      if (!parsed.patch || !parsed.patch.startsWith(SEASON_PATCH_PREFIX)) {
        state.skippedPatch++;
        return;
      }

      // Garde la trace du riot_match_id pour le dedupe
      parsed.riot_match_id = matchId;

      onGame(parsed);
      state.imported++;
    } catch (e) {
      // v7.7 fix : sur 429, on attend 30s puis on réessaie UNE fois
      if (isRateLimitError(e) && !isRetry && !signal.aborted) {
        state.phase = 'rate_limited';
        emit();
        try {
          await sleep(RATE_LIMIT_BACKOFF_MS, signal);
        } catch {
          return; // aborted pendant le backoff
        }
        state.phase = 'importing';
        emit();
        return importOneMatch(matchId, true); // retry une fois
      }
      console.error('[seasonImport] error on', matchId, e?.message || e);
      state.errors++;
    }
  }

  async function processBatch(batch) {
    // Lance les imports en parallèle (chaque importOneMatch fait 2 calls API)
    await Promise.all(batch.map(id => importOneMatch(id)));
  }

  async function run() {
    state.startedAt = Date.now();
    state.phase = 'listing';
    emit();

    try {
      // 1. Liste tous les match IDs de la saison
      const ids = await listAllMatchIds();
      if (signal.aborted) {
        state.phase = 'aborted';
        emit();
        return state;
      }

      // Filtre les IDs déjà en DB pour ne calculer le total que sur les nouveaux
      const toImport = ids.filter(id => !existingMatchIds.has(id));
      state.matchIds = toImport;
      state.total = toImport.length;
      state.skippedDedupe = ids.length - toImport.length;
      state.phase = 'importing';
      emit();

      // 2. Process par batch de BATCH_SIZE matchs, 1 batch / BATCH_INTERVAL_MS
      //    BATCH_SIZE(2) * 2 calls = 4 calls / 6s = 80 calls / 2min < 100 ✅
      //    (voir bloc de constantes en haut du fichier pour le détail du calcul)
      for (let i = 0; i < toImport.length; i += BATCH_SIZE) {
        if (signal.aborted) {
          state.phase = 'aborted';
          emit();
          return state;
        }
        const batch = toImport.slice(i, i + BATCH_SIZE);
        const batchStart = Date.now();
        await processBatch(batch);
        state.current = Math.min(i + BATCH_SIZE, toImport.length);
        emit();

        // Si pas le dernier batch, on attend le reste de l'intervalle
        if (i + BATCH_SIZE < toImport.length) {
          const elapsed = Date.now() - batchStart;
          const remaining = BATCH_INTERVAL_MS - elapsed;
          if (remaining > 0) {
            await sleep(remaining, signal);
          }
        }
      }

      state.phase = 'done';
      emit();
    } catch (e) {
      if (e.message === 'aborted') {
        state.phase = 'aborted';
      } else {
        console.error('[seasonImport] fatal:', e);
        state.phase = 'error';
        state.errors++;
      }
      emit();
    }

    return state;
  }

  function abort() {
    state.aborted = true;
    aborter.abort();
  }

  return { run, abort, state, signal };
}
